/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import { useEffect, useMemo, useReducer } from "react";
import dayjs from "dayjs";
import { When } from "react-if";
import { useParams } from "react-router-dom";
import { Button, theme } from "@nordcloud/gnui";
import { GroupsForResourceSortFields, SortOrder } from "~/generated/graphql";
import { Mode, Sidebar } from "~/components";
import { FilterSidebarFooter } from "~/components/SidebarWithMultiSelect/FilterSidebarFooter";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableDateRange } from "~/components/SidebarWithMultiSelect/SelectableDateRange";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { dateFormat } from "~/constants";
import { useDisclosure, useQueryState } from "~/hooks";
import {
  getFilterSidebarState,
  getLivePreviewFilterSidebarFooterState,
  hasFilterChanges,
  isNotEmpty,
  isNotNil,
} from "~/tools";
import { resourceDetailsResourceGroupsExcludeFilterMap } from "~/tools/filterStateMaps";
import { useGetGroupsForResource } from "../../hooks/useGetGroupsForResource/useGetGroupsForResource";
import { useGetGroupUniqueAttributes } from "../../hooks/useGetGroupsUniqueAttributes/useGetGroupsUniqueAttributes";
import { ResourceDetailsResourceGroupsQuery } from "../../types/ResourceDetailsResourceGroupsQuery";
import { prepareResourceDetailsResourceGroupsFilter } from "./resourceDetailsResourceGroupsFilterBuilders";

type State = {
  startDate?: string;
  endDate?: string;
  createdBy?: string[];
  excludeCreatedBy?: boolean;
};
type ResourceDetailsResourceGroupsQueryState =
  ResourceDetailsResourceGroupsQuery & {
    page?: string | number;
    limit?: string | number;
  };

function getCreatedByCheckboxes(createdBy: string[] | undefined) {
  return (
    createdBy?.map((creator) => ({
      value: creator,
      label: creator,
    })) ?? []
  );
}

function getNormalizedPhrase(phrase: string | undefined) {
  return isNotEmpty(phrase?.trim() || "") ? phrase : undefined;
}

function buildState(queryState: ResourceDetailsResourceGroupsQuery) {
  return {
    startDate: queryState.startDateUTC
      ? dayjs(queryState.startDateUTC).format(dateFormat.shortDate)
      : undefined,
    endDate: queryState.endDateUTC
      ? dayjs(queryState.endDateUTC).format(dateFormat.shortDate)
      : undefined,
    createdBy: queryState.createdBy,
    excludeCreatedBy: queryState.excludeCreatedBy,
  };
}

function buildQueryStateFromSidebarState(
  queryState: ResourceDetailsResourceGroupsQueryState,
  state: State
): ResourceDetailsResourceGroupsQueryState {
  return {
    ...queryState,
    startDateUTC: state.startDate
      ? dayjs(state.startDate).format(dateFormat.fullDate)
      : undefined,
    endDateUTC: state.endDate
      ? dayjs(state.endDate).endOf("day").format(dateFormat.fullDate)
      : undefined,
    createdBy: state.createdBy,
    excludeCreatedBy: state.excludeCreatedBy,
  };
}

export function ResourceDetailsResourceGroupsFiltersSidebar({
  resultsCount = 0,
}: {
  resultsCount?: number;
}) {
  const { isOpen, open, close } = useDisclosure();
  const { queryState, updateQueryState } =
    useQueryState<ResourceDetailsResourceGroupsQuery>();
  const { resourceId } = useParams<{ resourceId: string }>();

  const { data: fetchedData, loading } = useGetGroupUniqueAttributes();

  const createdByCheckboxes = getCreatedByCheckboxes(fetchedData?.createdBy);

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({ ...data, ...partialData }),
    buildState(queryState)
  );

  useEffect(() => {
    updateState(buildState(queryState));
  }, [queryState, isOpen]);
  const appliedState = buildState(queryState);
  const hasPendingChanges = hasFilterChanges(
    state,
    appliedState,
    resourceDetailsResourceGroupsExcludeFilterMap
  );
  const draftQueryState = useMemo(
    () => buildQueryStateFromSidebarState(queryState, state),
    [queryState, state]
  );
  const phrase = getNormalizedPhrase(draftQueryState.searchPhrase);
  const { count: previewCount, loading: previewLoading } =
    useGetGroupsForResource({
      resourceId: resourceId ?? "",
      page: String(draftQueryState.page ?? "1"),
      limit: String(draftQueryState.limit ?? "20"),
      sort: {
        field: draftQueryState.field ?? GroupsForResourceSortFields.Name,
        order: draftQueryState.order ?? SortOrder.Asc,
      },
      filter: prepareResourceDetailsResourceGroupsFilter(
        draftQueryState,
        phrase
      ),
      skip: !isOpen || !hasPendingChanges,
    });
  const previewFooterState = getLivePreviewFilterSidebarFooterState({
    hasPendingChanges,
    isOpen,
    previewLoading,
    previewResultsCount: previewLoading ? undefined : previewCount,
    appliedResultsCount: resultsCount,
    entityLabel: "resource groups",
  });

  const applyFilters = () => {
    const nextQueryState = buildQueryStateFromSidebarState(queryState, state);
    updateQueryState({
      ...nextQueryState,
      limit: String(nextQueryState.limit ?? queryState.limit ?? "20"),
      page: "1",
    });
    close();
  };

  const clearAllFilters = () => {
    updateState({
      startDate: undefined,
      endDate: undefined,
      createdBy: undefined,
      excludeCreatedBy: undefined,
    });
    updateQueryState({
      ...queryState,
      startDateUTC: undefined,
      endDateUTC: undefined,
      createdBy: undefined,
      excludeCreatedBy: undefined,
      page: "1",
    });
    close();
  };

  const clearDateCreatedFilter = () => {
    updateState({ startDate: undefined, endDate: undefined });
  };

  const clearCreatedByFilter = () => {
    updateState({ createdBy: undefined, excludeCreatedBy: undefined });
  };
  const filterSidebarState = getFilterSidebarState({
    currentState: state,
    appliedState,
    resultsCount: previewFooterState.effectiveResultsCount,
    entityLabel: "resource groups",
    excludeFilterMap: resourceDetailsResourceGroupsExcludeFilterMap,
  });
  const helperText =
    previewFooterState.helperTextOverride ?? filterSidebarState.helperText;

  return (
    <>
      <Button
        mr={theme.spacing.spacing04}
        severity="low"
        icon="filter"
        onClick={open}
      >
        Filters
      </Button>
      <When condition={isOpen}>
        <Sidebar
          title="Filter Results"
          isOpen={isOpen}
          onClick={close}
          footer={
            <FilterSidebarFooter
              helperText={helperText}
              isApplyDisabled={filterSidebarState.isApplyDisabled}
              isClearDisabled={filterSidebarState.isClearDisabled}
              onApply={applyFilters}
              onClear={clearAllFilters}
            />
          }
        >
          <FilterWrapper
            name="Date Created"
            isSelected={!!state?.startDate && !!state?.endDate}
            selectedOptions={["startDate", "endDate"]}
            onClear={clearDateCreatedFilter}
          >
            <SelectableDateRange
              selectedRange={{
                startDate: state.startDate,
                endDate: state.endDate,
              }}
              onRangeSelect={(range) => updateState(range)}
              label="Time range when resource group was created"
              setDefaultEndDateToToday
            />
          </FilterWrapper>
          <FilterWrapper
            name="Created By"
            isSelected={
              isNotNil(state.createdBy) && isNotEmpty(state.createdBy)
            }
            selectedOptions={state.createdBy ?? []}
            onClear={clearCreatedByFilter}
          >
            <SelectableListWithSearch
              isLoading={loading}
              allowModeChange
              selectedOptions={state.createdBy ?? []}
              selectableItems={createdByCheckboxes}
              isExcludeSelected={state.excludeCreatedBy}
              onMultiSelect={(createdBy) => updateState({ createdBy })}
              onModeChange={(mode) =>
                updateState({ excludeCreatedBy: mode === Mode.EXCLUDES })
              }
            />
          </FilterWrapper>
        </Sidebar>
      </When>
    </>
  );
}
